import React from 'react';
import styles from './GlobalMicroButton.module.css';

type ButtonVariant = 'primary' | 'secondary';
type IconPosition = 'left' | 'right';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  isLoading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: IconPosition;
  destructive?: boolean;
  hideTextOnMobile?: boolean;
}

const GlobalMicroButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className = '',
      variant = 'primary',
      isLoading = false,
      disabled,
      icon,
      iconPosition = 'left',
      destructive,
      hideTextOnMobile = false,
      children,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || isLoading;
    const buttonClasses = [
      styles.button,
      styles[variant],
      isLoading ? styles.loading : '',
      destructive ? styles.destructive : '',
      hideTextOnMobile ? styles.hideTextOnMobile : '',
      className,
    ].join(' ');

    const renderIcon = () => {
      if (!icon) return null;
      return (
        <span
          className={`${styles.icon} ${
            iconPosition === 'left' ? styles.iconLeft : styles.iconRight
          }`}
          aria-hidden="true"
        >
          {icon}
        </span>
      );
    };

    return (
      <button
        ref={ref}
        className={buttonClasses}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        aria-busy={isLoading}
        {...props}
      >
        {iconPosition === 'left' && renderIcon()}
        {children}
        {iconPosition === 'right' && renderIcon()}
      </button>
    );
  }
);

GlobalMicroButton.displayName = 'GlobalMicroButton';

export { GlobalMicroButton };
